import { type HTMLAttributes, type ReactNode, forwardRef, useEffect, useState } from 'react';

import { cn } from '../utils/cn';

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Panel title
   */
  title?: string;

  /**
   * Panel description
   */
  description?: string;

  /**
   * Whether the panel is collapsible
   * @default false
   */
  collapsible?: boolean;

  /**
   * Whether the panel is collapsed (only works if collapsible is true)
   * @default false
   */
  collapsed?: boolean;

  /**
   * Callback when collapse state changes
   */
  onCollapsedChange?: (collapsed: boolean) => void;

  /**
   * Actions to display in the header
   */
  actions?: ReactNode;

  /**
   * Panel variant
   * @default 'default'
   */
  variant?: 'default' | 'bordered' | 'flat';
}

/**
 * Panel component
 *
 * A collapsible panel component for organizing content sections.
 * Similar to Card but with collapse functionality.
 *
 * @example
 * ```tsx
 * <Panel title="Settings" collapsible>
 *   <div>Panel content</div>
 * </Panel>
 *
 * <Panel
 *   title="Advanced Options"
 *   description="Configure advanced settings"
 *   actions={<Button size="sm">Edit</Button>}
 * >
 *   <div>Content</div>
 * </Panel>
 * ```
 */
export const Panel = forwardRef<HTMLDivElement, PanelProps>(
  (
    {
      title,
      description,
      collapsible = false,
      collapsed = false,
      onCollapsedChange,
      actions,
      variant = 'default',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [isCollapsed, setIsCollapsed] = useState(collapsed);

    useEffect(() => {
      setIsCollapsed(collapsed);
    }, [collapsed]);

    const handleToggle = () => {
      const newCollapsed = !isCollapsed;
      setIsCollapsed(newCollapsed);
      onCollapsedChange?.(newCollapsed);
    };

    const baseStyles = ['rounded-lg transition-all duration-200'];

    const variantStyles = {
      default: 'bg-white border border-gray-200',
      bordered: 'bg-white border-2 border-gray-300',
      flat: 'bg-gray-50',
    };

    return (
      <div ref={ref} className={cn(baseStyles, variantStyles[variant], className)} {...props}>
        {(title || description || actions || collapsible) && (
          <div className="flex items-start justify-between p-4 border-b border-gray-200">
            <div className="flex-1">
              {title && (
                <div className="flex items-center gap-2">
                  {collapsible && (
                    <button
                      type="button"
                      onClick={handleToggle}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                      aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                    >
                      <svg
                        className={cn(
                          'h-5 w-5 transition-transform duration-200',
                          isCollapsed ? 'rotate-0' : 'rotate-90'
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                </div>
              )}
              {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
            </div>
            {actions && <div className="ml-4">{actions}</div>}
          </div>
        )}
        {(!collapsible || !isCollapsed) && <div className="p-4">{children}</div>}
      </div>
    );
  }
);

Panel.displayName = 'Panel';

export interface PanelSectionProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section description
   */
  description?: string;

  /**
   * Whether to add a divider above the section
   * @default true
   */
  divider?: boolean;
}

/**
 * PanelSection component
 *
 * A section within a panel for organizing content.
 */
export const PanelSection = forwardRef<HTMLDivElement, PanelSectionProps>(
  ({ title, description, divider = true, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(divider && 'border-t border-gray-200 pt-4 mt-4', className)}
        {...props}
      >
        {(title || description) && (
          <div className="mb-3">
            {title && <h4 className="text-sm font-semibold text-gray-900">{title}</h4>}
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
        )}
        {children}
      </div>
    );
  }
);

PanelSection.displayName = 'PanelSection';
